import { WS_INACCURACY } from 'constants/params';
import { StoryBoardTracks } from 'services/storyboard.service';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { getClipTransition, isNull } from 'components/app/_helpers/_app.helper';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { useSbContext } from 'components/app/storyboard/_mediator/sb.provider';
import { Playback, setDampingPlayerIndex } from './sb-player.slice';
import { getPrevIndex } from './sb-player.helper';
const SRC = 'use-player-transition.ts';

const MIN_TRANSITION = 50;

// CSS
const VAR__DAMPING_OPACITY = '--damping-player-opacity';
const VAR__ACTIVE_OPACITY = '--active-player-opacity';
const VAR__TRANSITION = '--player-transition-time';

export const useSbPlayerTransition = (rootRef: React.RefObject<HTMLDivElement>) => {
  const { sbListenClip } = useSbContext();
  const dispatch = useAppDispatch();
  const redux = useAppStore();

  /* helpers */

  const setOpacity = (damping: Fraction, active: Fraction) => {
    rootRef.current!.style.setProperty(VAR__DAMPING_OPACITY, damping + '');
    rootRef.current!.style.setProperty(VAR__ACTIVE_OPACITY, active + '');
  };

  const setTransition = (transition: Seconds) => {
    rootRef.current!.style.setProperty(VAR__TRANSITION, transition + 's');
  };

  /* callbacks */

  const resetTransition = (cursorCell: Index) => {
    const { cells, storyTransition } = selectData(redux.getState());
    if (!cells || !storyTransition) {
      console.error(`[${SRC}]: resetTransition`);
      return;
    }
    const cellDuration = cells.duration[cursorCell + 1];
    if (cellDuration) setTransition(getClipTransition(cellDuration, storyTransition));
    setOpacity(1, 0);
  };

  const handleTransitionEnd = (isEmptyCell: boolean, activePlayerIndex: Index, cell: Index) => {
    resetTransition(cell);
    return isEmptyCell ? null : activePlayerIndex;
  };

  const handleTransition = (
    cursorCell: Index,
    cursorTime: EditorTime,
    track: StoryBoardTracks,
    data?: Omit<Playback, 'dampingPlayerIndex'>
  ) => {
    const { storyBoard, markers, cells, storyTransition, playback, sbPlayback, startMarkerId } = selectData(
      redux.getState()
    );
    const { activePlayerIndex, isEmptyCell, linkedGroupIndex } = data ?? playback;

    if (storyTransition <= MIN_TRANSITION) {
      return isEmptyCell ? null : activePlayerIndex;
    }
    if (sbListenClip.current?.clipId && !sbPlayback) {
      resetTransition(cursorCell);
      return activePlayerIndex;
    }
    if (cursorCell === 0 || cursorCell - 1 === startMarkerId) {
      resetTransition(cursorCell);
      return isEmptyCell ? null : activePlayerIndex;
    }
    if (!isNull(linkedGroupIndex) && linkedGroupIndex > 0) {
      resetTransition(cursorCell);
      return activePlayerIndex;
    }
    if (!markers || !cells || !storyTransition) {
      console.error(`[${SRC}] handleTransition`);
      return null;
    }
    const cellStartTime = markers[cursorCell - 1];
    const transitionDuration = getClipTransition(cells.duration[cursorCell], storyTransition);
    const cursorCellTime = cursorTime - cellStartTime;

    if (cursorCellTime > transitionDuration) {
      resetTransition(cursorCell);
      const dampingPlayerIndex = isEmptyCell ? null : activePlayerIndex;
      if (cursorCellTime - transitionDuration < WS_INACCURACY) {
        dispatch(setDampingPlayerIndex(dampingPlayerIndex));
        console.log(`[${SRC}] exception when the regionOut callback is not triggered`);
      }
      return dampingPlayerIndex;
    }

    const cursorTransitionDuration = transitionDuration - cursorCellTime;
    const opacityIn = cursorCellTime / transitionDuration;
    const opacityOut = 1 - opacityIn;
    setOpacity(opacityOut, opacityIn);
    setTransition(cursorTransitionDuration);

    if (storyBoard[track][cursorCell - 1] === EMPTY_CELL) return null;
    else return getPrevIndex(activePlayerIndex);
  };

  return { handleTransition, handleTransitionEnd };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { markers, cells, storyBoard, storyConfig } = state.editor;
  const { sbPlayback } = state.sb;
  const storyTransition = storyConfig!.transitionTime;
  const startMarkerId = storyConfig?.startMarkerId;
  return {
    storyTransition,
    sbPlayback,
    markers,
    cells,
    storyBoard,
    startMarkerId,
    playback: state.sb__player,
  };
};
